import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { dimensionLabel, rubricColor } from "../lib/scoring";
import MarkButton from "./MarkButton";

/**
 * Drill-down side panel — opens from the right when a heatmap cell is clicked.
 * Shows the 8 items for one (dimension × segment) pair with their Likert
 * distributions, raw means, and Top Box percentages.
 *
 * The panel intentionally re-fetches /api/deployments/{id}/scores with the
 * same filter the heatmap used to build the clicked cell. This guarantees
 * the dimension-level mean + Top Box rendered at the top of the panel match
 * the heatmap cell exactly (criterion 9). If they diverge, that's a bug in
 * the suppression/filter logic — not in the panel.
 *
 * Animation: 200ms ease-out slide-in, 150ms ease-in slide-out. Cell-to-cell
 * swap while the panel is open: no animation; content replaces instantly.
 *
 * Focus: on open, focus moves to the close button. On close, focus returns
 * to the heatmap cell that triggered the panel (via stable data-testid
 * selector — survives Heatmap re-renders).
 */

const LIKERT_LABELS = ["Strongly disagree", "Disagree", "Neutral", "Agree", "Strongly agree"];
const LIKERT_COLORS = ["#AD393E", "#D17C81", "#C6BFAE", "#7FAEBF", "#4592A8"];

export default function DrillDownPanel({
  deploymentId,
  dimensionKey,
  segmentKey,
  segmentValue,
  onClose,
  triggerSelector,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [phase, setPhase] = useState("entering");  // entering | open | leaving
  const closeButtonRef = useRef(null);
  const fetchKey = `${dimensionKey}::${segmentKey}::${segmentValue}`;

  // Slide-in: kick the next paint into "open" so the CSS transition runs.
  useEffect(() => {
    const t = requestAnimationFrame(() => setPhase("open"));
    return () => cancelAnimationFrame(t);
  }, []);

  // Fetch data for the current (dim, segment). Re-runs on cell-to-cell swap
  // because fetchKey changes. No animation between fetches.
  // setState calls live inside the async IIFE so they're deferred out of
  // the effect's synchronous body (satisfies react-hooks/set-state-in-effect).
  useEffect(() => {
    let cancelled = false;
    const filters = segmentKey === "_all" ? null : { [segmentKey]: segmentValue };
    const q = filters ? `?filters=${encodeURIComponent(JSON.stringify(filters))}` : "";
    (async () => {
      if (cancelled) return;
      setLoading(true);
      setError("");
      try {
        const { data: payload } = await api.get(`/api/deployments/${deploymentId}/scores${q}`);
        if (cancelled) return;
        if (payload.suppressed) {
          // This should not happen in practice — heatmap suppresses these cells
          // before they're clickable. Defensive only.
          setError(`Cohort suppressed (n=${payload.n}, need 5+).`);
          setData(null);
        } else {
          const dim = (payload.dimensions || []).find((d) => d.dimension === dimensionKey);
          const items = (payload.items || []).filter((i) => i.dimension === dimensionKey);
          items.sort((a, b) => a.item - b.item);
          setData({ n: payload.n, dim, items });
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [deploymentId, fetchKey, dimensionKey, segmentKey, segmentValue]);

  // Focus the close button when the panel first becomes interactive.
  useEffect(() => {
    if (phase === "open" && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [phase]);

  // Stable close: slide out, then refocus the trigger cell, then unmount.
  const requestClose = useCallback(() => {
    if (phase === "leaving") return;
    setPhase("leaving");
    setTimeout(() => {
      if (triggerSelector) {
        const el = document.querySelector(triggerSelector);
        if (el && typeof el.focus === "function") el.focus();
      }
      onClose();
    }, 150);
  }, [phase, triggerSelector, onClose]);

  // Escape closes
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") requestClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [requestClose]);

  const dimLabel = dimensionLabel(dimensionKey);
  const segmentLabel = segmentKey === "_all"
    ? "All Respondents"
    : `${segmentValue} (${segmentKey})`;

  const transform = phase === "open" ? "translateX(0)" : "translateX(100%)";
  const transition = phase === "entering"
    ? "transform 200ms ease-out"
    : phase === "leaving"
    ? "transform 150ms ease-in"
    : "transform 200ms ease-out";

  return (
    <>
      {/* Backdrop catches outside clicks for dismissal but stays nearly transparent
          so the heatmap remains visible underneath. */}
      <div
        onClick={requestClose}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(26,26,26,0.12)",
          zIndex: 900,
          opacity: phase === "open" ? 1 : 0,
          transition: "opacity 200ms ease-out",
        }}
        aria-hidden="true"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="drill-down-title"
        data-testid="drill-down-panel"
        className="bg-paper"
        style={{
          position: "fixed",
          top: 0, right: 0, bottom: 0,
          width: "min(540px, 100vw)",
          zIndex: 950,
          boxShadow: "-12px 0 28px rgba(0,0,0,0.12)",
          transform,
          transition,
          display: "flex",
          flexDirection: "column",
          borderLeft: "1px solid #E8E8E8",
        }}
      >
        {/* Header bar */}
        <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100" style={{ flexShrink: 0 }}>
          <div className="text-xs text-gray-500" style={{ fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase" }} data-testid="drill-breadcrumb">
            {segmentLabel} <span style={{ color: "#CCCCCC", margin: "0 8px" }}>›</span> {dimLabel}
          </div>
          <button
            ref={closeButtonRef}
            onClick={requestClose}
            data-testid="drill-close"
            aria-label="Close drill-down panel"
            style={{
              fontFamily: "Oswald",
              fontSize: "18px",
              color: "#555555",
              background: "transparent",
              border: 0,
              cursor: "pointer",
              padding: "4px 8px",
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Body — scrollable */}
        <div style={{ flex: 1, overflowY: "auto" }} className="px-7 py-6">
          {loading && (
            <div className="text-sm text-gray-500" data-testid="drill-loading">Loading…</div>
          )}
          {error && (
            <div className="panel" style={{ borderColor: "#AD393E" }} data-testid="drill-error">
              <div className="eyebrow mb-1" style={{ color: "#AD393E" }}>Error</div>
              <div className="text-sm">{error}</div>
            </div>
          )}
          {data && data.dim && (
            <>
              <DimensionSummary dim={data.dim} n={data.n} />
              <div className="mt-8 space-y-5" data-testid="drill-items">
                {data.items.map((item) => (
                  <ItemRow key={item.item} item={item} />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer — open the marked-items tray */}
        <div className="px-7 py-4 border-t border-gray-100 flex items-center justify-between" style={{ flexShrink: 0 }}>
          <div className="text-xs text-gray-500" style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}>
            Mark individual items below — they appear in the tray and on page 5.
          </div>
        </div>
      </aside>
    </>
  );
}

function DimensionSummary({ dim, n }) {
  const c = rubricColor(dim.rubric_level);
  return (
    <div className="panel" data-testid="drill-dim-summary">
      <h2 id="drill-down-title" className="mb-4" style={{ fontFamily: "Oswald", fontSize: "22pt", color: "#1A1A1A" }}>
        {dimensionLabel(dim.dimension)}
      </h2>
      <div className="grid grid-cols-3 gap-6 mb-3">
        <Stat label="Mean" value={dim.mean.toFixed(2)} testId="drill-summary-mean" />
        <Stat label="Top Box" value={`${Math.round(dim.tb_pct)}%`} testId="drill-summary-tb" />
        <Stat label="Respondents" value={n} testId="drill-summary-n" />
      </div>
      <div
        className="inline-block px-3 py-1 text-xs"
        style={{
          fontFamily: "Oswald",
          letterSpacing: "0.15em",
          textTransform: "uppercase",
          background: c.bg,
          color: c.fg,
        }}
        data-testid="drill-summary-rubric"
      >
        {dim.rubric_level}
      </div>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-xs text-gray-500 mb-1" style={{ fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase" }}>
        {label}
      </div>
      <div className="text-2xl text-ink" style={{ fontFamily: "Oswald", fontWeight: 700 }}>
        {value}
      </div>
    </div>
  );
}

function ItemRow({ item }) {
  // Reverse-scored items render identically — no visual indication (criterion 4).
  const total = item.counts.reduce((a, b) => a + b, 0);
  return (
    <div className="border-t border-gray-100 pt-4" data-testid={`drill-item-${item.item}`}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div style={{ flex: 1 }}>
          <div className="text-xs text-gray-500 mb-1" style={{ fontFamily: "Oswald", letterSpacing: "0.18em" }}>
            ITEM {item.item}
          </div>
          <ItemStatement text={item.text} />
        </div>
        <div className="flex flex-col items-end gap-2" style={{ minWidth: "84px" }}>
          <div
            className="text-xl px-3 py-1"
            style={{
              fontFamily: "Oswald", fontWeight: 700,
              background: "#FAF7F1", color: "#1A1A1A", display: "inline-block",
            }}
            data-testid={`drill-item-${item.item}-mean`}
          >
            {item.mean.toFixed(2)}
          </div>
          <div className="text-xs text-gray-500" style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}>
            <span data-testid={`drill-item-${item.item}-tb`}>{Math.round(item.tb_pct)}%</span> top box
          </div>
          <MarkButton
            kind="item"
            payload={{ item_number: item.item }}
            testIdSuffix={`drill-${item.item}`}
          />
        </div>
      </div>
      {total === 0 ? (
        <div className="text-xs text-gray-500 italic">No responses</div>
      ) : (
        <DistributionBar counts={item.counts} total={total} itemNumber={item.item} />
      )}
    </div>
  );
}

function ItemStatement({ text }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <button
      onClick={() => setExpanded((x) => !x)}
      className="text-left text-sm text-ink"
      style={{
        background: "transparent",
        border: 0,
        padding: 0,
        cursor: "pointer",
        display: "-webkit-box",
        WebkitLineClamp: expanded ? "unset" : 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        lineHeight: 1.45,
      }}
      title={expanded ? "Click to collapse" : "Click to show full statement"}
    >
      {text}
    </button>
  );
}

function DistributionBar({ counts, total, itemNumber }) {
  return (
    <div data-testid={`drill-item-${itemNumber}-distribution`}>
      <div className="flex" style={{ height: "10px", background: "#F0EDE5", overflow: "hidden" }}>
        {counts.map((c, i) => {
          const pct = total ? (c / total) * 100 : 0;
          if (pct === 0) return null;
          return (
            <div
              key={i}
              style={{
                width: `${pct}%`,
                background: LIKERT_COLORS[i],
              }}
              title={`${LIKERT_LABELS[i]}: ${c} (${Math.round(pct)}%)`}
            />
          );
        })}
      </div>
      <div className="flex justify-between mt-1" style={{ fontFamily: "Oswald", fontSize: "9pt", letterSpacing: "0.05em", color: "#777777" }}>
        {counts.map((c, i) => (
          <div key={i} style={{ flex: 1, textAlign: "center" }}>
            {Math.round((total ? c / total : 0) * 100)}%
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-0.5" style={{ fontSize: "8pt", color: "#AAAAAA" }}>
        <span>SD</span><span>D</span><span>N</span><span>A</span><span>SA</span>
      </div>
    </div>
  );
}
